import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { blue } from '@material-ui/core/colors';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import SatelliteChooser from '../../components/acquisition/SatelliteChooser';
import AOIChooser from '../../components/bathymetry/AOIChooser';
import PeriodChooser from '../../components/bathymetry/PeriodChooser';
import BathymetryBandsAndAlgorithmSelection from '../../components/bathymetry/BathymetryBandsAndAlgorithmSelection';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import { useFile } from '../../contexts/index.jsx';
import Footer from '../../components/core/Footer';
import { Actions as Auth } from '../../../store/ducks/auth';
import { useStepNavigations, FIRST } from '../../../hooks/useStepNavigations';

/**
 * Module for the bathymetry process
 * @module Bathymetry
 */

/**
 * @constant MODULE_ACCENT
 * @description Bathymetry's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:Bathymetry
 */
const MODULE_ACCENT = blue[400];

const STEPS = [
  {
    label: 'forms.acquisition.1.title',
    content: 'forms.acquisition.1.description',
    /**
     * Wrapped rather than passed straight through: `SatelliteChooser` is shared by every module and
     * `makeRoutes` renders it with a fixed set of props, so the redesigned look is opted into here.
     */
    component: (props) => <SatelliteChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.acquisition.2.labelBathymetryStep',
    content: '',
    requires: 'satellite',
    component: AOIChooser,
  },
  {
    label: 'forms.acquisition.3.labelBathymetryStep',
    content: 'forms.acquisition.3.bathymetry.description',
    component: PeriodChooser,
  },
  {
    skip: false,
    label: 'forms.acquisition.4.labelBathymetryStep',
    content: 'forms.acquisition.4.contentBathymetryStep',
    component: BathymetryBandsAndAlgorithmSelection,
  },
];

/**
 * @function Bathymetry
 * @description Component for the bathymetry data acquisition process.
 * The acquisition process is divided into multiple steps, each represented by a separate component.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * **Called by:
 * @see {MainPage} - via the `/bathymetry/:step?` Route.
 * @memberof module:Bathymetry
 * @returns {JSX.Element} Rendered Bathymetry acquisition page component.
 */
const Bathymetry = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const { acquisitionData, navigate, navigateTo, stepAttendsRequirements, canRevisitStep, makeRoutes } =
    useStepNavigations(match, STEPS, '/main/bathymetry/result');
  const { fileData } = useFile();
  const imagePeriods = useMemo(() => {
    const periodState = fileData?.periodState;
    if (!periodState) return undefined;
    if (periodState.showCompositeInterval) {
      const selected = (periodState.compositeImageList || []).filter(
        (image) => periodState.selectedCompositeImages?.[image.id] && image.date,
      );
      return selected.length ? [selected] : undefined;
    }
    return periodState.dateSingleImg ? [[{ date: periodState.dateSingleImg }]] : undefined;
  }, [fileData]);

  const [stepOverrides, setStepOverrides] = useState({});
  const steps = STEPS.map((el, i) => ({ ...el, ...stepOverrides[i] }));

  /**
   * @function onSkipChange
   * @description Handler for updating the skip status of a step. This allows dynamically marking steps as skippable based on user interactions or requirements.
   * @param {boolean} value - The new skip status for the step (true if the step should be skipped, false otherwise)
   * @param {number} indexStep - The index of the step to update in the STEPS array
   * **Called by:
   * @see {AOIChooser} - via the onSkipChange prop, when the preset-mode toggle changes.
   * @memberof module:Bathymetry
   * @returns {void} This function does not return a value; it updates the component's state to reflect the new skip status of the specified step.
   */
  const onSkipChange = (value, indexStep) => {
    setStepOverrides((prev) => ({
      ...prev,
      [indexStep]: { ...prev[indexStep], skip: value },
    }));
  };

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  const step = hasStep ? match.params.step - 1 : 0;

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h4" align="center">
              {t('forms.acquisition.title')}
            </Typography>
          </div>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={steps.map((el, i) => ({
                id: String(i),
                label: t(el.label),
                disabled: !canRevisitStep(i),
                skipped: Boolean(el.skip),
              }))}
              currentStep={String(step)}
              onStepClick={(i) => canRevisitStep(i) && navigateTo(i)}
            />
            <StepSummary acquisition={acquisitionData} imagePeriods={imagePeriods} className={classes.summary} />
            {STEPS[step].content && (
              <Typography className={classes.instructions} variant="subtitle1" align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Divider />
            <Switch>
              {makeRoutes(hasStep, {
                ...STEPS[step]?.props,
                onSkipChange,
              })}
            </Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
}));

export default Bathymetry;
